import type { ScopeOrganization, ScopeTenant } from '@workspace/scope-context';
import {
  listCatalogProducts,
  listCatalogs,
  listCommerceChannels,
  listMarketGroups,
  listMarkets,
  listPublications,
  type Catalog,
  type CatalogPublication,
  type CommerceChannel,
  type Market,
  type MarketGroup,
  type Product,
  type ServiceabilityZone,
} from '@/lib/commerce';
import type { CommerceSelectOptionViewModel } from '@workspace/silk-ui';

type ScopeCollectionKind = 'organizations' | 'tenants' | 'stores' | 'agents';

function pushUniqueOption(
  map: Map<string, CommerceSelectOptionViewModel>,
  option: CommerceSelectOptionViewModel,
) {
  if (!option.value || map.has(option.value)) {
    return;
  }
  map.set(option.value, option);
}

export function buildPublicationSelectorOptions(
  publications: CatalogPublication[],
): CommerceSelectOptionViewModel[] {
  return publications.map(publication => ({
    value: publication.publication_id,
    label: publication.storefront_key || publication.catalog_key || publication.publication_id,
    description: publication.scope,
    helperText: publication.status,
    keywords: [
      publication.publication_id,
      publication.catalog_id,
      publication.catalog_key || '',
      publication.storefront_key || '',
      publication.scope,
      publication.status,
    ].filter(Boolean),
  }));
}

export function buildChannelSelectorOptions(
  channels: CommerceChannel[],
): CommerceSelectOptionViewModel[] {
  return channels.map(channel => ({
    value: channel.channel_id,
    label: channel.name || channel.channel_key || channel.channel_id,
    description: channel.channel_key,
    helperText: channel.status,
    keywords: [
      channel.channel_id,
      channel.channel_key,
      channel.name,
      channel.kind,
      channel.status,
    ].filter(Boolean),
  }));
}

export function buildTenantSelectorOptions(args: {
  activeTenant?: ScopeTenant | null;
  organizations?: ScopeOrganization[];
  observedTenantIds?: string[];
  directoryTenants?: Array<{
    id: string;
    name?: string | null;
    slug?: string | null;
    status?: string | null;
    accountId?: string | null;
    organizationId?: string | null;
  }>;
}): CommerceSelectOptionViewModel[] {
  const { activeTenant, organizations = [], observedTenantIds = [], directoryTenants = [] } = args;
  const options = new Map<string, CommerceSelectOptionViewModel>();

  if (activeTenant?.id) {
    pushUniqueOption(options, {
      value: activeTenant.id,
      label: activeTenant.name || activeTenant.id,
      description: 'Active tenant',
      groupLabel: 'Current Scope',
      keywords: [activeTenant.slug || '', activeTenant.accountId || '', activeTenant.organizationId || ''].filter(Boolean),
    });
  }

  organizations.forEach(organization => {
    const groupLabel = organization.name || organization.id;
    (organization.tenants || []).forEach(tenant => {
      pushUniqueOption(options, {
        value: tenant.id,
        label: tenant.name || tenant.id,
        description: organization.name || organization.id,
        groupLabel,
        helperText: tenant.status || null,
        keywords: [tenant.slug || '', tenant.accountId || '', tenant.organizationId || '', organization.slug || ''].filter(Boolean),
      });
    });
  });

  directoryTenants.forEach(tenant => {
    pushUniqueOption(options, {
      value: tenant.id,
      label: tenant.name || tenant.id,
        description: tenant.organizationId || 'Account tenant',
        groupLabel: 'Account',
        helperText: tenant.status || null,
        keywords: [tenant.slug || '', tenant.accountId || '', tenant.organizationId || ''].filter(Boolean),
      });
  });

  observedTenantIds.forEach(tenantId => {
    pushUniqueOption(options, {
      value: tenantId,
      label: tenantId,
      description: 'Observed commerce tenant',
      groupLabel: 'Observed',
    });
  });

  return Array.from(options.values());
}

export function buildObservedScopeOptions(
  publications: CatalogPublication[],
  kind: ScopeCollectionKind,
): CommerceSelectOptionViewModel[] {
  const options = new Map<string, CommerceSelectOptionViewModel>();

  publications.forEach(publication => {
    const ids =
      kind === 'organizations'
        ? publication.organization_ids || []
        : kind === 'tenants'
          ? publication.selling_tenant_ids || []
          : kind === 'stores'
            ? publication.store_ids || []
            : publication.agent_user_ids || [];

    ids.forEach(id => {
      pushUniqueOption(options, {
        value: id,
        label: id,
        description: publication.storefront_key || publication.catalog_key || publication.publication_id,
        helperText: 'Observed in publication access',
        groupLabel: 'Observed',
        keywords: [publication.publication_id, publication.catalog_id, publication.scope].filter(Boolean),
      });
    });
  });

  return Array.from(options.values());
}

export function buildCatalogSelectorOptions(catalogs: Catalog[]): CommerceSelectOptionViewModel[] {
  return catalogs.map(catalog => ({
    value: catalog.catalog_id,
    label: catalog.name,
    description: catalog.catalog_key,
    helperText: catalog.status,
    keywords: [catalog.catalog_key, catalog.catalog_id, catalog.default_currency || ''].filter(Boolean),
  }));
}

export function buildServiceabilityZoneSelectorOptions(
  zones: ServiceabilityZone[],
): CommerceSelectOptionViewModel[] {
  return zones.map(zone => ({
    value: zone.zone_id,
    label: zone.name,
    description: zone.zone_key,
    helperText: zone.source_system || zone.zone_kind || null,
    keywords: [zone.zone_id, zone.zone_key, zone.source_ref || '', zone.source_system || ''].filter(Boolean),
  }));
}

export function buildMarketSelectorOptions(
  markets: Market[],
  zonesById?: Map<string, string>,
): CommerceSelectOptionViewModel[] {
  return markets.map(market => ({
    value: market.market_id,
    label: market.name,
    description: market.market_key,
    helperText: zonesById?.get(market.zone_id) || market.status,
    keywords: [market.market_id, market.market_key, market.zone_id, market.status, market.install_method || ''].filter(Boolean),
  }));
}

export function buildMarketGroupSelectorOptions(
  groups: MarketGroup[],
): CommerceSelectOptionViewModel[] {
  return groups.map(group => ({
    value: group.market_group_id,
    label: group.name,
    description: group.market_group_key,
    helperText: group.market_count ? `${group.market_count} markets` : null,
    keywords: [group.market_group_id, group.market_group_key, ...(group.market_labels || [])].filter(Boolean),
  }));
}

export function buildProductSelectorOptions(
  products: Product[],
  catalogsById?: Map<string, Catalog>,
): CommerceSelectOptionViewModel[] {
  return products.map(product => {
    const catalog = catalogsById?.get(product.catalog_id);
    return {
      value: product.product_id,
      label: product.name,
      description: product.product_key,
      helperText: catalog?.name || product.service_class || product.status,
      groupLabel: catalog?.name || undefined,
      keywords: [
        product.product_id,
        product.product_key,
        product.catalog_id,
        product.service_class || '',
        product.service_group || '',
        product.product_type_key || '',
      ].filter(Boolean),
    };
  });
}

export function buildVariantSelectorOptions(
  products: Product[],
  catalogsById?: Map<string, Catalog>,
): CommerceSelectOptionViewModel[] {
  return products.flatMap(product =>
    product.variants.map(variant => ({
      value: variant.variant_id,
      label: variant.display_name || variant.name,
      description: `${product.name} / ${variant.variant_key}`,
      helperText: catalogsById?.get(product.catalog_id)?.name || variant.status,
      groupLabel: catalogsById?.get(product.catalog_id)?.name || product.name,
      badge: variant.sellable ? 'Sellable' : 'Hidden',
      keywords: [
        variant.variant_id,
        variant.variant_key,
        variant.product_id,
        product.product_id,
        product.product_key,
        variant.billing_period || '',
        variant.fulfillment_code || '',
      ].filter(Boolean),
    })),
  );
}

function buildUniqueScalarOptions(
  values: string[],
  groupLabel: string,
): CommerceSelectOptionViewModel[] {
  return Array.from(new Set(values.filter(Boolean)))
    .sort((a, b) => a.localeCompare(b))
    .map(value => ({
      value,
      label: value,
      description: groupLabel,
      groupLabel,
      keywords: [value],
    }));
}

export function buildServiceClassSelectorOptions(products: Product[]) {
  return buildUniqueScalarOptions(
    products.map(product => product.service_class || ''),
    'Service Classes',
  );
}

export function buildServiceGroupSelectorOptions(products: Product[]) {
  return buildUniqueScalarOptions(
    products.map(product => product.service_group || ''),
    'Service Groups',
  );
}

export type CommerceSelectorRecords = {
  publications: CatalogPublication[];
  channels: CommerceChannel[];
  catalogs: Catalog[];
  products: Product[];
  markets: Market[];
  groups: MarketGroup[];
};

export async function loadCommerceSelectorRecords(
  catalogOwnerTenantId: string,
): Promise<CommerceSelectorRecords> {
  const [publications, channels, catalogs, markets, groups] = await Promise.all([
    listPublications({ catalogOwnerTenantId, limit: 200 }).then(page => page.items),
    listCommerceChannels({ catalogOwnerTenantId }),
    listCatalogs({ catalogOwnerTenantId, limit: 200 }).then(page => page.items),
    listMarkets({ catalogOwnerTenantId, limit: 200 }).then(response => response.items),
    listMarketGroups({ catalogOwnerTenantId, limit: 200 }).then(response => response.items),
  ]);

  const productResults = await Promise.all(
    catalogs.map(catalog =>
      listCatalogProducts(catalog.catalog_id, { limit: 200, include_variants: true })
        .then(response => response.items)
        .catch(() => [] as Product[]),
    ),
  );

  return {
    publications,
    channels,
    catalogs,
    products: productResults.flat(),
    markets,
    groups,
  };
}
